import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue
} from "react-hook-form";

import fontWeight from "../../../../styles/mui/fontWeight";
import colors from "../../../../styles/colors/colors";
import DatePicker from "../../../../components/formComponents/DatePicker";
import { PhoneFaxExtInput } from "../../../../components/formComponents/PhoneFaxExtInput";
import {
  BoxStyle,
  MemberDetailsHeading,
  RadioGroupContainer,
  RadioGroupStyle,
} from "./style";
import AddressFormInputUseForm from "../../../../components/formComponents/AddressFormInputUseForm";
import EmergencyContact from "../../../../components/formComponents/EmergencyContact";
import {
  LanguageMasterResponse,
  RelationshipMasterResponse,
} from "../../../../models/Api/MemberDetails";
import { TabSessionStorageResponse } from "../../../../models/Api/TabSessionStorage";
import { rules } from "../../../../utils/validations/Validations";
import ReadOnlyField from "../../../../components/formComponents/ReadOnlyField";
import {
  DropDownType,
  RelationshipMaster,
} from "../../../../models/MemberDetails";
import { GENDER_DROPDOWN_OPTIONS } from "../../../../constants/MemberDetails";
import { FormInputText } from "../../../../components/formComponents/FormInputText";
import { width100PCT } from "../../../../styles/mui/styles/widths";
import { getValue } from "../../../../utils";
import {
  EligibilityInformation,
} from "../../../../models/CheckEligibility";

export interface PropsFromState {
  control: Control<any, any>;
  reset: any;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  LanguageMasterData: LanguageMasterResponse;
  TabSessionStorageData: TabSessionStorageResponse;
  relationMasterData: RelationshipMasterResponse;
  eligibilityData: EligibilityInformation | null;
}
export interface PropsFromDispatch {
  getLanguageMasterResp: () => void;
  getRelationshipMasterResp: () => void;
}

type AllProps = PropsFromState & PropsFromDispatch;
const MemberDetails: React.FC<any> = ({
  control,
  reset,
  setValue,
  getValues,
  LanguageMasterData,
  getLanguageMasterResp,
  relationMasterData,
  getRelationshipMasterResp,
  eligibilityData,
}: AllProps) => {
  const labels = [
    {
      label: "Street 1: ",
      name: "patientDetail.residentialAddress.streetName1",
      helper: rules.streetName1,
      textLength: 100,
    },
    {
      label: "Street 2: ",
      name: "patientDetail.residentialAddress.streetName2",
      helper: rules.streetName2,
      textLength: 100,
    },
    {
      label: "City: ",
      name: "patientDetail.residentialAddress.city",
      helper: rules.cityRequired,
      textLength: 100,
    },
    {
      label: "State: ",
      name: "patientDetail.residentialAddress.patientState",
      helper: rules.stateRequired,
      textLength: 100,
    },
    {
      label: "County: ",
      name: "patientDetail.residentialAddress.county",
      helper: rules.physicianCounty,
      textLength: 100,
    },
    {
      label: "Postal Code: ",
      name: "patientDetail.residentialAddress.zipCode",
      helper: rules.zipcode,
      textLength: 10,
    },
  ];
  const { loading: LanguageLoading, response: LanguageResponse } =
    LanguageMasterData;
  const { loading: RelationLoading, response: RelationResponse } =
    relationMasterData;
  const [languageData, setLanguageData] = useState<any[]>();
  const [eligibilityStatus, setEligibilityStatus] = useState<boolean | null>();
  const [relationData, setRelationData] = useState<RelationshipMaster[]>();
  useEffect(() => {
    if (!LanguageLoading) {
      getLanguageMasterResp();
    }
    if (!RelationLoading) {
      getRelationshipMasterResp();
    }
  }, []);

  useEffect(() => {
    setEligibilityStatus(getValue(eligibilityData, "eligibilityStatus"));
  }, [eligibilityData]);

  useEffect(() => {
    if (LanguageResponse) {
      setLanguageData(LanguageResponse);
    }
    if (RelationResponse) {
      setRelationData(RelationResponse);
    }
  }, [LanguageResponse, RelationResponse]);

  const searchInput = useRef(null);

  const onRadioChange = (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setValue(e.target.name, value, { shouldValidate: true });
  }

  return (
    <Grid container pl={"2rem"}>
      {!eligibilityStatus && (
        <>
          <Grid size={12}>
            <Grid container>
              <Grid size={12} sx={MemberDetailsHeading}>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={fontWeight.Weight[4]}
                    color={colors.black[2]}
                  >
                    Member Details
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Grid container pb={"1.2rem"}>
              <Grid size={3} pr={"3rem"}>
                <ReadOnlyField
                  label={"Last Name:"}
                  value={getValues("patientDetail.lastName")}
                  mandatorySymbol={true}
                  borderBottom={true}
                />
              </Grid>
              <Grid size={3} pr={"3rem"}>
                <FormControl variant="standard" fullWidth>
                  <FormInputText
                    name={`patientDetail.firstName`}
                    control={control}
                    textLength={30}
                    helper={rules.firstName}
                    label="First Name:"
                  />
                </FormControl>
              </Grid>
              <Grid size={3} pr={"3rem"}>
                <FormControl variant="standard" sx={width100PCT}>
                  <DatePicker
                    name="patientDetail.dateOfBirth"
                    control={control}
                    label={"Date of Birth:"}
                    readOnly={true}
                    helper={rules.dateOfBirth}
                  />
                </FormControl>
              </Grid>
              <Grid size={3} pr={"3rem"}>
                <PhoneFaxExtInput
                  control={control}
                  phoneFaxName={`patientDetail.phone`}
                  rules={rules.phone}
                  label="Phone:"
                />
              </Grid>
            </Grid>
            <Grid container sx={RadioGroupContainer}>
              <Grid size={3}>
                <Box sx={{ display: "flex" }}>
                  <Typography
                    variant="body1"
                    fontWeight={fontWeight.Weight[3]}
                    color={colors.fonts[2]}
                  >
                    Gender:
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={fontWeight.Weight[3]}
                    color={colors.red[100]}
                    pl={"0.2rem"}
                  >
                    *
                  </Typography>
                </Box>
                <Controller
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <Box>
                      <>
                        <RadioGroup
                          aria-labelledby="controlled-radio-buttons-group"
                          sx={RadioGroupStyle}
                          {...field}
                          onChange={onRadioChange}
                        >
                          {GENDER_DROPDOWN_OPTIONS.map(
                            (singleOption: DropDownType) => (
                              <Box key={singleOption.value} sx={BoxStyle}>
                                <FormControlLabel
                                  value={singleOption.value}
                                  label={
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight={fontWeight.Weight[5]}
                                      color={colors.black[5]}
                                    >
                                      {singleOption.label}
                                    </Typography>
                                  }
                                  control={<Radio color="primary" />}
                                />
                              </Box>
                            )
                          )}
                        </RadioGroup>
                        <FormHelperText>
                          {error ? error.message : null}
                        </FormHelperText>
                      </>
                    </Box>
                  )}
                  name={`patientDetail.gender`}
                  control={control}
                  rules={rules.gender}
                />
              </Grid>
              <Grid size={4}>
                <Box sx={{ display: "flex" }}>
                  <Typography
                    variant="body1"
                    fontWeight={fontWeight.Weight[3]}
                    color={colors.fonts[2]}
                  >
                    Member Preferred Language:
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={fontWeight.Weight[3]}
                    color={colors.red[100]}
                    pl={"0.2rem"}
                  >
                    *
                  </Typography>
                </Box>
                <Controller
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <Box>
                      <>
                        <RadioGroup
                          aria-labelledby="controlled-radio-buttons-group"
                          sx={RadioGroupStyle}
                          {...field}
                          onChange={onRadioChange}
                        >
                          {languageData &&
                            languageData.map(
                              (singleOption: any) => (
                                <Box key={singleOption.languageId} sx={BoxStyle}>
                                  <FormControlLabel
                                    value={singleOption.languageId}
                                    label={
                                      <Typography
                                        variant="subtitle1"
                                        fontWeight={fontWeight.Weight[5]}
                                        color={colors.black[5]}
                                      >
                                        {singleOption.languageName}
                                      </Typography>
                                    }
                                    control={<Radio color="primary" />}
                                  />
                                </Box>
                              )
                            )}
                        </RadioGroup>
                        <FormHelperText>
                          {error ? error.message : null}
                        </FormHelperText>
                      </>
                    </Box>
                  )}
                  name={`patientDetail.languageId`}
                  control={control}
                  rules={rules.language}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Grid container pt={"0.3rem"}>
              <Grid size={12} sx={RadioGroupContainer}>
                <AddressFormInputUseForm
                  control={control}
                  searchInput={searchInput}
                  reset={reset}
                  getValues={getValues}
                  setValue={setValue}
                  labels={labels}
                  addressLabel="Address Details:"
                  addressVariant="h6"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12} sx={RadioGroupContainer}>
            <Grid container pt={"0.3rem"}>
              <Grid size={12}>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={fontWeight.Weight[4]}
                    color={colors.black[2]}
                  >
                    Plan Details
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Grid container>
              <Grid size={3} pt={"1rem"} pr={"3rem"}>
                <ReadOnlyField
                  label={"Health Plan Name:"}
                  value={getValues("patientDetail.eligibilityPayerName")}
                  mandatorySymbol={true}
                  borderBottom={true}
                />
              </Grid>
              <Grid size={3} pt={"1rem"} pr={"3rem"}>
                <ReadOnlyField
                  label={"Subscriber ID:"}
                  value={getValues("patientDetail.subscriberId")}
                  mandatorySymbol={true}
                  borderBottom={true}
                />
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
      <Grid size={12} pb={"1rem"}>
        <EmergencyContact
          control={control}
          relationData={relationData}
          setValue={setValue}
          getValues={getValues}
          reset={reset}
        />
      </Grid>
    </Grid>
  );
};

export default MemberDetails;
